/**
 * Interfaz para el almacenamiento en caché de un token
 */
interface CachedToken {
  token: string;
  timestamp: number;
  expiresIn: number; // Tiempo de expiración en milisegundos
}

/**
 * Servicio que proporciona caché para tokens de formulario
 * Implementa el patrón Singleton para ser accesible globalmente
 */
export class TokenCache {
  private static instance: TokenCache;
  private cache: Map<string, CachedToken>;
  private pendingRequests: Map<string, Promise<string>>;
  
  // Tiempo por defecto de expiración de tokens: 15 minutos
  private readonly DEFAULT_EXPIRATION = 15 * 60 * 1000;
  
  private constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }
  
  /**
   * Obtiene la instancia única del servicio de caché
   */
  public static getInstance(): TokenCache {
    if (!TokenCache.instance) {
      TokenCache.instance = new TokenCache();
    }
    return TokenCache.instance;
  }
  
  /**
   * Obtiene un token del caché o ejecuta la función para obtenerlo
   * @param key - Clave única para identificar el token
   * @param fetchFunction - Función asíncrona que obtiene el token del servidor
   * @param expiresIn - Tiempo de expiración del token en milisegundos
   * @returns Promesa con el token
   */
  public async getToken(
    key: string, 
    fetchFunction: () => Promise<string>,
    expiresIn = this.DEFAULT_EXPIRATION
  ): Promise<string> {
    // Verificar si ya hay un token válido en caché
    const cachedToken = this.cache.get(key);
    const now = Date.now();
    
    if (cachedToken && cachedToken.timestamp + cachedToken.expiresIn > now) {
      return cachedToken.token;
    }
    
    // Verificar si ya hay una solicitud en curso para este token
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }
    
    // Si no hay token en caché ni solicitud en curso, crear una nueva solicitud
    
    // Crear la promesa y guardarla en las solicitudes pendientes
    const tokenPromise = fetchFunction()
      .then(token => {
        // Guardar el token en caché
        this.cache.set(key, {
          token,
          timestamp: Date.now(),
          expiresIn
        });
        
        // Eliminar la solicitud de las pendientes
        this.pendingRequests.delete(key);
        
        return token;
      })
      .catch(error => {
        // En caso de error, eliminar la solicitud de las pendientes
        this.pendingRequests.delete(key);
        throw error;
      });
    
    // Guardar la promesa para poder reutilizarla si hay solicitudes simultáneas
    this.pendingRequests.set(key, tokenPromise);
    
    return tokenPromise;
  }
  
  /**
   * Elimina un token del caché
   * @param key - Clave del token a eliminar
   */
  public invalidateToken(key: string): void {
    this.cache.delete(key);
  }
  
  /**
   * Limpia todo el caché de tokens
   */
  public clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
} 